import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { FirestoreError } from "firebase/firestore";
import type { ZodTypeAny, infer as zInfer } from "zod";
import { useUserId } from "@/features/auth/hooks/useUserId";
import { makeDisabledQueryKey, makeQueryKey } from "./queryKey";
import { createFirestoreQueryFn } from "./subscription";
import type {
  FirestoreKey,
  Options,
  UseFirestoreResponse,
  WithKeyPath,
} from "./types";

const noop = () => {};

const NON_RETRYABLE_CODES = new Set(["permission-denied", "unauthenticated"]);

const shouldRetry = (failureCount: number, error: Error) => {
  if (error instanceof FirestoreError && NON_RETRYABLE_CODES.has(error.code)) {
    return false;
  }
  return failureCount < 3;
};

const useFirestore = <
  TModel extends ZodTypeAny,
  TKeyPath extends string | undefined,
  TKeyPathValue = string,
>(
  key: FirestoreKey,
  model: TModel,
  options: Options<TKeyPath> = {},
): UseFirestoreResponse<
  WithKeyPath<zInfer<TModel>, TKeyPath, TKeyPathValue>
> => {
  const userId = useUserId();
  const queryKey = makeQueryKey(key, options.query, userId ?? "");
  // user-scoped queries are torn down and purged on logout (AuthHooks)
  const isUserScoped = typeof key === "string" && key.includes("{userId}");

  const result = useQuery({
    queryKey: queryKey ?? makeDisabledQueryKey(key),
    queryFn: createFirestoreQueryFn<
      WithKeyPath<zInfer<TModel>, TKeyPath, TKeyPathValue>
    >(model, options),
    enabled: Boolean(queryKey),
    meta: { userScoped: isUserScoped },
    staleTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false, // the SDK reconnects its own listeners
    retry: shouldRetry,
    placeholderData: keepPreviousData,
  });

  return {
    data: queryKey ? result.data : undefined,
    error: result.error ?? undefined,
    isLoading: Boolean(queryKey) && result.isPending,
    isLagging: Boolean(queryKey) && result.isPlaceholderData,
    resetLaggy: noop, // retained for API compatibility; no consumers
  };
};

export { useFirestore };
